'use client';

import { use, useState, useEffect, ChangeEvent } from 'react';
import { notFound, redirect } from 'next/navigation';
import Link from 'next/link';
import {
    ArrowLeft,
    Building2,
    MapPin,
    Phone,
    Mail,
    Globe,
    Award,
    Calendar,
    Users,
    User,
    FileText,
    CheckCircle2,
    Plus,
    Download,
    GraduationCap,
    Lock,
    List
} from 'lucide-react';
import { getSchoolName, SchoolUnit } from '@/lib/data';
import RichTextEditor from '@/components/ui/RichTextEditor';

interface SchoolDetail {
    npsn: string;
    address: string;
    phone: string;
    email: string;
    website: string;
    headmaster: string;
    accreditation: string;
    established: string;
    students: number;
    teachers: number;
    staff: number;
    vision: string;
    mission: string;
}

// Mock Data for Basic Information
const schoolDetails: Record<string, SchoolDetail> = {
    'paud': {
        npsn: '12345678',
        address: 'Jl. Ahmad Yani No. 123, Pekanbaru',
        phone: '(0761) 123456',
        email: 'paud.alazhar@example.com',
        website: 'www.paudalazhar.sch.id',
        headmaster: 'Siti Aminah, S.Pd',
        accreditation: 'A',
        established: '2010',
        students: 120,
        teachers: 15,
        staff: 5,
        vision: 'Mewujudkan generasi usia dini yang cerdas, ceria, dan berakhlak mulia.',
        mission: '1. Menyelenggarakan pendidikan yang menyenangkan.\n2. Mengembangkan potensi anak secara optimal.\n3. Menanamkan nilai-nilai agama sejak dini.'
    },
    'paud-pekanbaru': {
        npsn: '12345679',
        address: 'Jl. Sudirman No. 45, Pekanbaru',
        phone: '(0761) 654321',
        email: 'paud.pku@example.com',
        website: 'www.paudpku.sch.id',
        headmaster: 'Mariana, S.Pd',
        accreditation: 'A',
        established: '2012',
        students: 85,
        teachers: 10,
        staff: 3,
        vision: 'Menjadi pusat pendidikan anak usia dini unggulan di Pekanbaru.',
        mission: 'Memberikan pelayanan pendidikan terbaik bagi anak usia dini.'
    },
    'paud-kampar': {
        npsn: '12345680',
        address: 'Jl. Raya Bangkinang Km 5, Kampar',
        phone: '(0762) 112233',
        email: 'paud.kampar@example.com',
        website: 'www.paudkampar.sch.id',
        headmaster: 'Rina Wati, S.Pd',
        accreditation: 'B',
        established: '2015',
        students: 60,
        teachers: 8,
        staff: 2,
        vision: 'Mencetak generasi emas Kampar.',
        mission: 'Melaksanakan pembelajaran yang kreatif dan inovatif.'
    },
    'paud-siak': {
        npsn: '12345681',
        address: 'Jl. Sultan Syarif Kasim No. 10, Siak',
        phone: '(0764) 445566',
        email: 'paud.siak@example.com',
        website: 'www.paudsiak.sch.id',
        headmaster: 'Dewi Sartika, S.Pd',
        accreditation: 'B',
        established: '2016',
        students: 55,
        teachers: 7,
        staff: 2,
        vision: 'Membangun karakter anak bangsa di Siak.',
        mission: 'Pendidikan berbasis karakter dan budaya lokal.'
    },
    'sd': {
        npsn: '22345678',
        address: 'Jl. Soekarno Hatta No. 88, Pekanbaru',
        phone: '(0761) 223344',
        email: 'sd.alazhar@example.com',
        website: 'www.sdalazhar.sch.id',
        headmaster: 'Budi Santoso, M.Pd',
        accreditation: 'A',
        established: '2005',
        students: 450,
        teachers: 35,
        staff: 10,
        vision: 'Menjadi SD Islam Terpadu yang unggul dalam prestasi dan akhlak mulia.',
        mission: '1. Mengintegrasikan kurikulum nasional dan nilai-nilai Islam.\n2. Mengembangkan bakat dan minat siswa.\n3. Menjalin kerjasama dengan orang tua dan masyarakat.'
    },
    'smp': {
        npsn: '32345678',
        address: 'Jl. Arifin Achmad No. 99, Pekanbaru',
        phone: '(0761) 334455',
        email: 'smp.alazhar@example.com',
        website: 'www.smpalazhar.sch.id',
        headmaster: 'Drs. H. Ahmad Yusuf, M.Pd',
        accreditation: 'A',
        established: '2000',
        students: 380,
        teachers: 40,
        staff: 12,
        vision: 'Mewujudkan lulusan yang beriman, berilmu, dan beramal.',
        mission: '1. Melaksanakan pembelajaran yang aktif, inovatif, kreatif, dan menyenangkan.\n2. Membiasakan perilaku Islami di lingkungan sekolah.\n3. Meningkatkan prestasi akademik dan non-akademik.'
    },
    'smk': {
        npsn: '42345678',
        address: 'Jl. HR. Soebrantas Km 12, Pekanbaru',
        phone: '(0761) 445566',
        email: 'smk.alazhar@example.com',
        website: 'www.smkalazhar.sch.id',
        headmaster: 'Ir. H. Muhammad Ridwan, MT',
        accreditation: 'A',
        established: '1998',
        students: 620,
        teachers: 55,
        staff: 15,
        vision: 'Menghasilkan lulusan SMK yang kompeten, profesional, dan siap kerja.',
        mission: '1. Mengembangkan kurikulum berbasis industri.\n2. Melaksanakan pembelajaran berbasis kompetensi.\n3. Menjalin kerjasama dengan dunia usaha dan dunia industri.'
    }
};

export default function BasicInfoPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit } = use(params);
    const validUnits: SchoolUnit[] = ['paud', 'paud-pekanbaru', 'paud-kampar', 'paud-siak', 'sd', 'smp', 'smk'];

    if (!validUnits.includes(unit as SchoolUnit)) {
        notFound();
    }

    // Determine initial active unit (default to 'paud-pekanbaru' if visiting main 'paud' page)
    const initialUnit = unit === 'paud' ? 'paud-pekanbaru' : unit;

    // State management
    const [activeUnit, setActiveUnit] = useState<string>(initialUnit);
    const [isEditing, setIsEditing] = useState(false);
    const [showNextPeriodModal, setShowNextPeriodModal] = useState(false);
    const [formData, setFormData] = useState<SchoolDetail>(schoolDetails[activeUnit] || schoolDetails[unit]);

    // Update form data when activeUnit changes
    useEffect(() => {
        const newDetails = schoolDetails[activeUnit];
        if (newDetails) {
            setFormData(newDetails);
        }
        setIsEditing(false);
    }, [activeUnit]);

    const schoolName = getSchoolName(activeUnit as SchoolUnit);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? Number(value) : value
        }));
    };

    const handleSave = () => {
        // Here you would typically save to backend
        console.log('Saving mock data for', activeUnit, ':', formData);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setFormData(schoolDetails[activeUnit]);
        setIsEditing(false);
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-24 animate-in fade-in duration-700">
            {/* Modal for Next Period Headmaster */}
            {showNextPeriodModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-surface w-full max-w-md rounded-4xl border border-border-main p-8 shadow-2xl animate-in zoom-in-95 duration-300">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-foreground tracking-tight">Periode Selanjutnya</h3>
                            <button onClick={() => setShowNextPeriodModal(false)} className="w-10 h-10 rounded-full hover:bg-background flex items-center justify-center transition-colors">
                                <FileText className="w-5 h-5 text-muted" />
                            </button>
                        </div>
                        <div className="space-y-6">
                            <div className="text-center p-6 bg-primary-soft/30 rounded-3xl border border-primary/10">
                                <div className="w-20 h-20 bg-primary-dark mx-auto rounded-full flex items-center justify-center mb-4 shadow-lg">
                                    <User className="w-10 h-10 text-white" />
                                </div>
                                <h4 className="font-bold text-lg text-primary-dark">Drs. H. M. Nasir, M.Si</h4>
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest mt-1">Calon Kepala Sekolah</p>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Periode Jabatan</p>
                                    <p className="font-bold text-foreground">2026 - 2030</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Status Seleksi</p>
                                    <span className="px-3 py-1 bg-success-soft text-success text-[10px] font-bold rounded-full uppercase tracking-wider border border-success/10">Terverifikasi</span>
                                </div>
                            </div>
                            <button onClick={() => setShowNextPeriodModal(false)} className="w-full py-4 bg-primary text-white rounded-2xl font-bold text-sm hover:shadow-xl hover:shadow-primary/20 transition-all">Tutup Detail</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header Section */}
            <section className="bg-surface p-6 md:p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary-soft/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none transition-opacity duration-700 opacity-50 group-hover:opacity-100"></div>

                <div className="relative z-10">
                    <Link href="/yayasan" className="inline-flex items-center gap-2 text-secondary hover:text-primary transition-colors mb-6 group/back">
                        <ArrowLeft className="w-4 h-4 group-hover/back:-translate-x-1 transition-transform" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">Kembali ke Dashboard Unit</span>
                    </Link>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center gap-5">
                            <div className="w-16 h-16 bg-primary-soft text-primary rounded-2xl flex items-center justify-center shrink-0 shadow-inner">
                                <Building2 className="w-8 h-8" />
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">Informasi Dasar</h1>
                                <div className="flex items-center gap-2 mt-2">
                                    <span className="text-secondary text-sm font-medium">{schoolName}</span>
                                    <span className="w-1 h-1 rounded-full bg-border-main"></span>
                                    <span className="text-muted text-xs">NPSN: {formData.npsn}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            {isEditing ? (
                                <>
                                    <button
                                        onClick={handleCancel}
                                        className="px-5 py-3 bg-error-soft text-error rounded-2xl text-[10px] font-bold hover:bg-error hover:text-white transition-all uppercase tracking-widest"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        className="px-5 py-3 bg-success text-white rounded-2xl text-[10px] font-bold hover:shadow-lg hover:shadow-success/25 transition-all uppercase tracking-widest flex items-center gap-2"
                                    >
                                        <CheckCircle2 className="w-4 h-4" /> Simpan
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="px-5 py-3 bg-primary text-white rounded-2xl text-[10px] font-bold hover:shadow-lg hover:shadow-primary/25 transition-all uppercase tracking-widest flex items-center gap-2"
                                >
                                    <FileText className="w-4 h-4" /> Edit Informasi
                                </button>
                            )}
                        </div>
                    </div>

                    {/* School Selector for PAUD Units */}
                    {unit === 'paud' && (
                        <div className="mt-8 pt-6 border-t border-border-main">
                            <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-3">Pilih Unit PAUD</p>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { id: 'paud-pekanbaru', label: 'PAUD Pekanbaru' },
                                    { id: 'paud-kampar', label: 'PAUD Kampar' },
                                    { id: 'paud-siak', label: 'PAUD Siak' }
                                ].map((paudUnit) => (
                                    <button
                                        key={paudUnit.id}
                                        onClick={() => setActiveUnit(paudUnit.id)}
                                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${activeUnit === paudUnit.id
                                            ? 'bg-primary text-white shadow-lg shadow-primary/25'
                                            : 'bg-background hover:bg-primary-soft/50 text-secondary'
                                            }`}
                                    >
                                        {paudUnit.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Identity Information */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-surface rounded-4xl border border-border-main shadow-sm p-8 group">
                        <h2 className="text-lg font-bold mb-6 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary transition-transform group-hover:scale-110">
                                <Building2 className="w-4 h-4" />
                            </div>
                            Identitas Sekolah
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
                            <div>
                                <h3 className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2">Nama Unit</h3>
                                <p className="text-base font-semibold text-foreground">{schoolName}</p>
                            </div>
                            <div>
                                <h3 className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2">NPSN</h3>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="npsn"
                                        value={formData.npsn}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 rounded-xl border border-border-main bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm font-semibold transition-all"
                                    />
                                ) : (
                                    <p className="text-base font-semibold text-foreground tracking-wider">{formData.npsn}</p>
                                )}
                            </div>
                            <div>
                                <h3 className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2">Tanggal Berdiri</h3>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="established"
                                        value={formData.established}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 rounded-xl border border-border-main bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm font-semibold transition-all"
                                    />
                                ) : (
                                    <p className="text-base font-semibold text-foreground">{formData.established}</p>
                                )}
                            </div>
                            <div>
                                <h3 className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2">Akreditasi</h3>
                                {isEditing ? (
                                    <select
                                        name="accreditation"
                                        value={formData.accreditation}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 rounded-xl border border-border-main bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm font-semibold appearance-none transition-all cursor-pointer"
                                    >
                                        <option value="A">A</option>
                                        <option value="B">B</option>
                                        <option value="C">C</option>
                                        <option value="-">Belum Terakreditasi</option>
                                    </select>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <span className="w-8 h-8 rounded-lg bg-success-soft text-success font-bold flex items-center justify-center border border-success/20 shadow-xs">
                                            {formData.accreditation}
                                        </span>
                                        <span className="text-xs text-muted">Terakreditasi BAN-SM</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="h-px bg-border-main my-8"></div>

                        <h2 className="text-lg font-bold mb-6 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                                <MapPin className="w-4 h-4" />
                            </div>
                            Lokasi & Kontak
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
                            <div className="md:col-span-2">
                                <h3 className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2">Alamat</h3>
                                {isEditing ? (
                                    <textarea
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        rows={2}
                                        className="w-full px-4 py-2 rounded-xl border border-border-main bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm font-semibold resize-none transition-all"
                                    />
                                ) : (
                                    <p className="text-base font-semibold text-foreground">{formData.address}</p>
                                )}
                            </div>
                            <div>
                                <h3 className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2">Telepon</h3>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 rounded-xl border border-border-main bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm font-semibold transition-all"
                                    />
                                ) : (
                                    <div className="flex items-center gap-2 text-foreground font-semibold">
                                        <Phone className="w-4 h-4 text-muted" />
                                        {formData.phone}
                                    </div>
                                )}
                            </div>
                            <div>
                                <h3 className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2">Email</h3>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 rounded-xl border border-border-main bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm font-semibold transition-all"
                                    />
                                ) : (
                                    <div className="flex items-center gap-2 text-foreground font-semibold">
                                        <Mail className="w-4 h-4 text-muted" />
                                        {formData.email}
                                    </div>
                                )}
                            </div>
                            <div className="md:col-span-2">
                                <h3 className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2">Website</h3>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="website"
                                        value={formData.website}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 rounded-xl border border-border-main bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm font-semibold transition-all"
                                    />
                                ) : (
                                    <div className="flex items-center gap-2 text-primary font-semibold hover:underline cursor-pointer group/web w-fit">
                                        <Globe className="w-4 h-4 transition-transform group-hover/web:rotate-12" />
                                        {formData.website}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bg-surface rounded-4xl border border-border-main shadow-sm p-8 group">
                        <h2 className="text-lg font-bold mb-6 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                                <Award className="w-4 h-4" />
                            </div>
                            Visi & Misi
                        </h2>

                        <div className="space-y-8">
                            <div>
                                <h3 className="text-[10px] font-bold text-muted uppercase tracking-widest mb-3 flex items-center gap-2">
                                    Visi Sekolah
                                    {isEditing && <span className="text-[9px] bg-primary text-white px-2 py-0.5 rounded font-bold uppercase">Rich Text Mode</span>}
                                </h3>
                                {isEditing ? (
                                    <div className="space-y-2">
                                        <RichTextEditor
                                            value={formData.vision}
                                            onChange={(val) => setFormData(prev => ({ ...prev, vision: val }))}
                                            placeholder="Masukkan visi sekolah..."
                                        />
                                    </div>
                                ) : (
                                    <div className="bg-primary-soft/30 p-6 rounded-3xl border border-primary/10 relative">
                                        <div className="absolute -left-2 top-4 w-1 h-8 bg-primary rounded-full"></div>
                                        <div
                                            className="text-base font-semibold text-foreground italic leading-relaxed rich-text-content max-w-none"
                                            dangerouslySetInnerHTML={{ __html: formData.vision.startsWith('<') ? formData.vision : `"${formData.vision}"` }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div>
                                <h3 className="text-[10px] font-bold text-muted uppercase tracking-widest mb-3 flex items-center gap-2">
                                    Misi Sekolah
                                    {isEditing && <span className="text-[9px] bg-primary text-white px-2 py-0.5 rounded font-bold uppercase">Rich Text Mode</span>}
                                </h3>
                                {isEditing ? (
                                    <div className="space-y-2">
                                        <RichTextEditor
                                            value={formData.mission}
                                            onChange={(val) => setFormData(prev => ({ ...prev, mission: val }))}
                                            placeholder="Masukkan misi sekolah..."
                                        />
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {formData.mission.startsWith('<') ? (
                                            <div
                                                className="text-sm font-medium text-secondary leading-relaxed rich-text-content max-w-none"
                                                dangerouslySetInnerHTML={{ __html: formData.mission }}
                                            />
                                        ) : (
                                            formData.mission.split('\n').map((item, index) => (
                                                <div key={index} className="flex gap-4 group/misi">
                                                    <div className="mt-1.5 w-2 h-2 rounded-full bg-primary/40 group-hover/misi:bg-primary group-hover/misi:scale-125 transition-all shrink-0 shadow-sm"></div>
                                                    <p className="text-sm font-medium text-secondary leading-relaxed">{item.replace(/^\d+\.\s/, '')}</p>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Added Documents Section */}
                    <div className="bg-surface rounded-4xl border border-border-main shadow-sm p-8 group">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-bold flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-info-soft text-info flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <FileText className="w-4 h-4" />
                                </div>
                                Dokumen Sekolah
                            </h2>
                            <button className="px-4 py-2 bg-background border border-border-main rounded-xl text-[10px] font-bold uppercase tracking-widest hover:border-primary hover:text-primary transition-all flex items-center gap-2">
                                <Plus className="w-3.5 h-3.5" /> Unggah Dokumen
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                { name: 'Sertifikat Akreditasi 2024.pdf', size: '2.4 MB', type: 'PDF' },
                                { name: 'Izin Operasional Unit.pdf', size: '1.1 MB', type: 'PDF' },
                                { name: 'Profil Sekolah Lengkap.docx', size: '5.8 MB', type: 'DOCX' },
                            ].map((doc, idx) => (
                                <div key={idx} className="flex items-center gap-4 p-4 bg-background border border-border-main rounded-2xl hover:border-primary/30 transition-all cursor-pointer group/doc">
                                    <div className="w-10 h-10 rounded-xl bg-info-soft text-info flex items-center justify-center group-hover/doc:scale-110 transition-transform">
                                        <FileText className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-bold text-foreground truncate">{doc.name}</p>
                                        <p className="text-[10px] text-muted font-bold mt-0.5">{doc.type} • {doc.size}</p>
                                    </div>
                                    <button className="w-8 h-8 rounded-lg hover:bg-white flex items-center justify-center text-muted hover:text-primary transition-all">
                                        <Download className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Sidebar Stats and Headmaster */}
                <div className="space-y-6">
                    <div className="bg-surface rounded-4xl border border-border-main shadow-sm p-8 group">
                        <div className="text-center mb-8">
                            <div className="relative w-32 h-32 mx-auto mb-6">
                                <div className="absolute inset-0 bg-primary rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-opacity"></div>
                                <div className="relative w-full h-full bg-linear-to-br from-primary to-primary-dark rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
                                    <User className="w-14 h-14 text-white" />
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-success border-4 border-white rounded-full flex items-center justify-center text-white shadow-lg">
                                    <CheckCircle2 className="w-5 h-5" />
                                </div>
                            </div>

                            {isEditing ? (
                                <input
                                    type="text"
                                    name="headmaster"
                                    value={formData.headmaster}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 rounded-2xl border border-border-main bg-background focus:outline-none focus:ring-4 focus:ring-primary/10 text-base font-bold text-center mb-2 shadow-inner"
                                />
                            ) : (
                                <h3 className="font-bold text-xl text-foreground tracking-tight">{formData.headmaster}</h3>
                            )}
                            <p className="text-[10px] font-bold text-primary uppercase tracking-[0.2em] mb-6">Kepala Sekolah</p>

                            <div className="bg-background rounded-3xl p-5 border border-border-main shadow-inner space-y-3">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-bold text-muted uppercase tracking-widest">Status</span>
                                    <span className="font-bold text-success flex items-center gap-1.5">
                                        <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse"></div>
                                        Aktif
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-bold text-muted uppercase tracking-widest">Masa Jabatan</span>
                                    <span className="font-bold text-foreground tabular-nums tracking-wide">2022 - 2026</span>
                                </div>
                            </div>
                        </div>

                        {/* Revised Principal Modal Trigger */}
                        <button
                            onClick={() => setShowNextPeriodModal(true)}
                            className="w-full py-4 px-6 bg-surface border border-border-main rounded-2xl text-[10px] font-bold text-foreground hover:bg-primary-soft hover:text-primary hover:border-primary transition-all uppercase tracking-[0.15em] flex items-center justify-center gap-3 group/period"
                        >
                            <Calendar className="w-4 h-4 text-muted group-hover/period:text-primary group-hover/period:scale-110 transition-all" />
                            Cek Selanjutnya
                        </button>
                    </div>

                    <div className="bg-surface rounded-4xl border border-border-main shadow-sm p-8 group">
                        <h3 className="font-bold text-lg mb-6 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:rotate-12 transition-transform">
                                <Users className="w-4 h-4" />
                            </div>
                            Statistik SDM
                        </h3>

                        <div className="space-y-4">
                            {[
                                { label: 'Siswa Terdaftar', value: formData.students, icon: GraduationCap, color: 'text-info', bg: 'bg-info-soft' },
                                { label: 'Total Guru', value: formData.teachers, icon: User, color: 'text-warning', bg: 'bg-warning-soft' },
                                { label: 'Tenaga Staff', value: formData.staff, icon: User, color: 'text-success', bg: 'bg-success-soft' },
                            ].map((stat, idx) => (
                                <div key={idx} className="flex items-center justify-between p-5 bg-background rounded-3xl border border-border-main transition-all hover:scale-[1.02] hover:shadow-md cursor-default group/stat">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-2xl ${stat.bg} ${stat.color} flex items-center justify-center group-hover/stat:rotate-6 transition-transform shadow-sm`}>
                                            <stat.icon className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">{stat.label}</p>
                                            <p className="text-2xl font-black text-foreground tabular-nums group-hover/stat:text-primary transition-colors">{stat.value}</p>
                                        </div>
                                    </div>
                                    {/* Edit mode: Stats are now static as per user request */}
                                    {isEditing && (
                                        <Lock className="w-4 h-4 text-muted/30" />
                                    )}
                                </div>
                            ))}
                        </div>

                        {isEditing && (
                            <div className="mt-4 p-4 bg-muted-soft rounded-2xl border border-border-main text-center">
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest italic">Statistik diperbarui secara otomatis setiap periode</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <style jsx global>{`
                .rich-text-content ul {
                    list-style-type: disc !important;
                    padding-left: 1.5rem !important;
                    margin-bottom: 1rem !important;
                    display: block !important;
                }
                .rich-text-content ol {
                    list-style-type: decimal !important;
                    padding-left: 1.5rem !important;
                    margin-bottom: 1rem !important;
                    display: block !important;
                }
                .rich-text-content li {
                    margin-bottom: 0.5rem !important;
                    display: list-item !important;
                }
                .rich-text-content h1, .rich-text-content h2, .rich-text-content h3 {
                    font-weight: 700 !important;
                    margin-top: 1rem !important;
                    margin-bottom: 0.5rem !important;
                }
                .rich-text-content p {
                    margin-bottom: 0.75rem !important;
                }
            `}</style>
        </main>
    );
}
